import { queryOptions } from "@tanstack/react-query";
import { apiFetch } from "@/shared/api";
import type { SessionInfo, SessionMeta, SessionOwnerHistoryEntry } from "../types";

/** All sessions visible to the caller across every space they can see —
 *  drives the sidebar's «История» list. The badge per row comes from
 *  `space.name` in the response. */
export const sessionsQueryOptions = () =>
  queryOptions({
    queryKey: ["sessions"],
    queryFn: async (): Promise<SessionInfo[]> => {
      const resp = await apiFetch("/api/sessions", undefined, { crossSpace: true });
      if (!resp.ok) throw new Error("Не удалось загрузить сессии");
      return resp.json();
    },
    staleTime: 60_000,
  });

/** Sessions inside a single space — used by the space dashboard's
 *  «Недавние» panel. Identical shape; just scoped by header. */
export const sessionsInSpaceQueryOptions = (spaceId: string | null | undefined) =>
  queryOptions({
    queryKey: ["sessions", "in-space", spaceId],
    queryFn: async (): Promise<SessionInfo[]> => {
      const resp = await apiFetch("/api/sessions", {
        headers: spaceId ? { "X-Space-Id": spaceId } : undefined,
      }, { crossSpace: true });
      if (!resp.ok) throw new Error("Не удалось загрузить сессии");
      return resp.json();
    },
    enabled: !!spaceId,
    staleTime: 60_000,
  });

/** Per-session metadata: who's responsible, whether the caller can claim,
 *  and why claim might be blocked. Shorter staleTime than the list because
 *  the claimable flag depends on live run state. */
export const sessionMetaQueryOptions = (sessionId: string | null | undefined) =>
  queryOptions({
    queryKey: ["sessions", sessionId, "meta"],
    queryFn: async (): Promise<SessionMeta> => {
      const resp = await apiFetch(`/api/sessions/${sessionId}/meta`);
      if (!resp.ok) throw new Error("Не удалось загрузить сессию");
      return resp.json();
    },
    enabled: !!sessionId,
    staleTime: 5_000,
  });

export const sessionOwnersQueryOptions = (sessionId: string | null | undefined) =>
  queryOptions({
    queryKey: ["sessions", sessionId, "owners"],
    queryFn: async (): Promise<{ owners: SessionOwnerHistoryEntry[]; current_owner_id: string }> => {
      const resp = await apiFetch(`/api/sessions/${sessionId}/owners`);
      if (!resp.ok) throw new Error("Не удалось загрузить историю");
      return resp.json();
    },
    enabled: !!sessionId,
    staleTime: 30_000,
  });

export interface ClaimSessionResult {
  already_owner: boolean;
  owner_user_id: string;
}

export interface ClaimSessionError extends Error {
  status?: number;
  code?: "generation_in_progress" | "awaiting_user_input" | "not_session_owner" | string;
}

export async function claimSession(sessionId: string): Promise<ClaimSessionResult> {
  const resp = await apiFetch(`/api/sessions/${sessionId}/claim`, { method: "POST" });
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}));
    const detail = body?.detail;
    const err = new Error(
      typeof detail === "string"
        ? detail
        : detail?.message ?? "Не удалось взять сессию",
    ) as ClaimSessionError;
    err.status = resp.status;
    err.code = typeof detail === "object" ? detail?.code : undefined;
    throw err;
  }
  return resp.json();
}
